import type { CheckboxOnChangeArg, SelectOptions } from '@library/types/utils';
import {
  SELECT_ALL,
  getAllOption,
  getBgColor
} from '@library/component-utils/filters';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Float } from '@headlessui-float/react';
import { Listbox } from '@headlessui/react';
import { MultiSelectButton } from '@library/components/selects/multiSelect/MultiSelectButton';
import { MultiSelectOptions } from '@library/components/selects';
import { classNames } from '@library/utils';

export interface MultiSelectProps {
  allowSelectNone?: boolean;
  allOption?: boolean;
  customHeight?: string;
  disabled?: boolean;
  iconOnly?: boolean;
  options: SelectOptions;
  onChange: (value: SelectOptions) => void;
  portal?: boolean;
  label?: string;
  savingDisabled?: boolean;
  size: 'extrasmall' | 'small' | 'medium';
  showTags?: boolean;
  testId?: string;
  transparentBackground?: boolean;
  values: Array<string>;
  zIndex?: number;
  defaultEmpty?: boolean;
}

export const MultiSelect = ({
  allowSelectNone = false,
  allOption = true,
  customHeight = '',
  disabled = false,
  iconOnly = false,
  onChange,
  portal = false,
  label = '',
  options,
  savingDisabled = false,
  size,
  showTags = false,
  testId = 'multi-select',
  transparentBackground = false,
  values,
  zIndex = undefined,
  defaultEmpty = false
}: MultiSelectProps) => {
  const height = size === 'small' ? 'h-10' : 'h-11';
  const heightClass = customHeight || height;

  const allListOption = useMemo(
    () => getAllOption(label, defaultEmpty, values),
    [label, defaultEmpty, values]
  );
  const allOptions = useMemo(
    () => (allOption ? [allListOption, ...options] : options),
    [allListOption, allOption, options]
  );

  const selectValue = useCallback(
    (newValue: Array<string>) => {
      const selectedOptions = options.filter((option) =>
        newValue.includes(option.value)
      );

      if (defaultEmpty && !selectedOptions.length) {
        return [];
      }
      return (allowSelectNone && !selectedOptions.length) ||
        options.length === selectedOptions.length
        ? allOptions
        : selectedOptions;
    },
    [allowSelectNone, allOptions, defaultEmpty, options]
  );

  const [selectedValues, setSelectedValues] = useState<SelectOptions>(
    selectValue(values)
  );

  const selectedValuesWithoutAll = selectedValues.filter(
    (selectedValue) => selectedValue.key !== SELECT_ALL
  );

  const allSelected =
    options.length === selectedValuesWithoutAll.length ||
    selectedValuesWithoutAll.length === 0;

  const resetAll = (isOpen: boolean) => {
    if (!isOpen && !selectedValues.length) {
      setSelectedValues(defaultEmpty ? [] : allOptions);
    }
  };

  useEffect(() => {
    setSelectedValues(selectValue(values));
  }, [selectValue, options, values]);

  const handleOnChange = ({ id, checked }: CheckboxOnChangeArg) => {
    if (savingDisabled) {
      return;
    }
    if (id === SELECT_ALL) {
      if (checked) {
        if (!selectedValues.length) {
          setSelectedValues(allOptions);
          return;
        }

        onChange(options);
        return;
      }

      setSelectedValues([]);
      return;
    }

    const actualSelectedValues = selectedValues.filter(
      (selectedValue) => selectedValue.key !== SELECT_ALL
    );

    const excludeExclusives = actualSelectedValues.filter(
      (selected) => !selected.allOrExclusive
    );

    const selectedValue = options.find(
      (selectOption) => selectOption.key === id
    );

    if (!checked) {
      const filteredSelectedValues = selectedValue?.allOrExclusive
        ? actualSelectedValues
        : excludeExclusives;

      const newValue = filteredSelectedValues.filter(
        (selected) => selected.key !== id
      );

      if (allowSelectNone && !newValue.length) {
        onChange(options);
        return;
      }

      onChange(newValue);
      return;
    }

    if (selectedValue?.allOrExclusive) {
      onChange([selectedValue]);
      return;
    }

    onChange(
      selectedValue
        ? [...excludeExclusives, selectedValue]
        : actualSelectedValues
    );
  };

  const disableSelect = disabled || !options.length;

  return (
    <div className={classNames('group/ms relative max-w-[20rem]', heightClass)}>
      <Listbox
        disabled={disableSelect}
        value={selectedValues}
        by="key"
        multiple
      >
        {({ open }) => {
          return (
            <Float
              zIndex={zIndex ?? 9}
              offset={0}
              placement="bottom-start"
              strategy="absolute"
              portal={portal}
            >
              <Listbox.Button
                className={classNames(
                  transparentBackground
                    ? 'bg:transparent'
                    : getBgColor({ open, disabled: disableSelect }),
                  'border-soFresh-140 text-soClean-140 relative grid min-w-6 max-w-[20rem] grid-cols-[1fr_fit-content(5rem)] items-center justify-center rounded-md border text-left sm:text-sm',
                  disableSelect
                    ? 'cursor-default'
                    : 'hover:bg-soFresh-120 cursor-pointer',
                  iconOnly
                    ? 'group-hover/ms:border-soFresh-140 group-hover/ms:bg-soFresh-120 border-transparent bg-transparent'
                    : '',
                  iconOnly && open ? '!bg-soFresh-120 !border-soFresh-140' : '',
                  heightClass
                )}
                aria-disabled={disableSelect}
                data-testid={testId}
                onClick={() => resetAll(open)}
              >
                <MultiSelectButton
                  allSelected={allSelected}
                  allSelectedDisplayValue={allListOption.name}
                  firstOptionName={selectedValuesWithoutAll[0]?.name ?? ''}
                  iconOnly={iconOnly}
                  numberSelected={selectedValuesWithoutAll.length}
                  open={open}
                  label={label}
                  showTags={showTags}
                  size={size}
                />
              </Listbox.Button>
              <Listbox.Options className="bg-soFresh-120 ring-soFresh-100 focus:outline-hidden absolute top-1 z-10 size-max max-h-60 overflow-auto rounded-md py-1 text-base shadow-lg ring-1 sm:text-sm">
                <MultiSelectOptions
                  onChange={handleOnChange}
                  options={allOptions}
                  savingDisabled={savingDisabled}
                />
              </Listbox.Options>
            </Float>
          );
        }}
      </Listbox>
    </div>
  );
};
